import React, { useState, useMemo, useEffect } from 'react';
import { BiSolidPlaneAlt, BiSolidDownArrow } from 'react-icons/bi';
import FareDrawer from './FareDrawer';
import FlightDetailsDialog from '../flight/FlightDetailsDialog';
import SeatSelectionDialog from '../flight/SeatSelectionDialog';
import Amenities from '../ui/Amenities';
import { isDomesticUSARoute } from '../../utils/flightSearch';

const FlightResults = ({ selectedClass = 'Main', stopsFilter = 'Any number of stops', onClearFilters, searchResults = [], onFareSelection, fromCode, toCode, tripType = 'roundTrip', isRedeemMiles = false, onFlightSelect }) => {
  const [sortBy, setSortBy] = useState('');
  const [sortState, setSortState] = useState({}); // 'both', 'up', 'down'

  // Debug log for miles mode
  console.log("FlightResults isRedeemMiles:", isRedeemMiles);

  // Add visual indicator in console and page title when in miles mode
  useEffect(() => {
    if (isRedeemMiles) {
      console.log("🎯 MILES MODE ACTIVE - You should see the new grid layout!");
      document.title = "Flight Results - Miles Mode";
    } else {
      console.log("💰 CASH MODE ACTIVE - Standard layout");
      document.title = "Flight Results - Cash Mode";
    }
  }, [isRedeemMiles]);
  const [fareDrawer, setFareDrawer] = useState({ isOpen: false, flightId: null, flightData: null, fareType: null });
  const [flightDetailsDialog, setFlightDetailsDialog] = useState({ isOpen: false, flightData: null });
  const [seatSelectionDialog, setSeatSelectionDialog] = useState({ isOpen: false, flightData: null });

  // Check if this is a domestic USA route
  const isDomestic = isDomesticUSARoute(fromCode, toCode);

  // Get trip type display text
  const getTripTypeText = () => {
    return tripType === 'oneWay' ? 'One way from' : 'Round trip from';
  };

  const handleSort = (field) => {
    const currentState = sortState[field] || 'both';
    let newState;

    switch (currentState) {
      case 'both':
        newState = 'up';
        break;
      case 'up':
        newState = 'down';
        break;
      case 'down':
        newState = 'both';
        break;
      default:
        newState = 'both';
    }

    setSortState({ ...sortState, [field]: newState });
    setSortBy(field);
  };

  const handleFareDrawerOpen = (flight, fareType) => {
    // Toggle functionality: if same flight and same fare type, close drawer
    if (fareDrawer.isOpen && fareDrawer.flightId === flight.id && fareDrawer.fareType === fareType) {
      setFareDrawer({ isOpen: false, flightId: null, flightData: null, fareType: null });
    } else {
      // Open drawer with new flight data
      setFareDrawer({ isOpen: true, flightId: flight.id, flightData: flight, fareType });
    }
  };

  const handleFlightDetailsOpen = (flight) => {
    console.log("Opening flight details for:", flight);
    setFlightDetailsDialog({ isOpen: true, flightData: flight });
  };

  const handleFlightDetailsClose = () => {
    console.log("Closing flight details dialog");
    setFlightDetailsDialog({ isOpen: false, flightData: null });
  };

  const handleSeatSelectionOpen = (flight) => {
    console.log("Opening seat selection for:", flight);
    setSeatSelectionDialog({ isOpen: true, flightData: flight });
  };

  const handleSeatSelectionClose = () => {
    console.log("Closing seat selection dialog");
    setSeatSelectionDialog({ isOpen: false, flightData: null });
  };

  const handleFareDrawerClose = () => {
    setFareDrawer({ isOpen: false, flightId: null, flightData: null, fareType: null });
  };

  const handleFareSelection = (fareData, flightData) => {
    console.log('Fare selected in FlightResults:', fareData, flightData);
    // Call parent component's handler if provided
    if (onFareSelection) {
      onFareSelection(fareData, flightData);
    }
    setFareDrawer({ isOpen: false, flightId: null, flightData: null, fareType: null });
  };

  // Helper functions for sorting
  const parseTime = (timeString) => {
    const [time, period] = timeString.split(' ');
    const [hours, minutes] = time.split(':');
    let hour24 = parseInt(hours);
    if (period === 'PM' && hour24 !== 12) hour24 += 12;
    if (period === 'AM' && hour24 === 12) hour24 = 0;
    return hour24 * 60 + parseInt(minutes);
  };

  const parseDuration = (durationString) => {
    const parts = durationString.replace(/[^\d]/g, ' ').trim().split(/\s+/);
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;
    return hours * 60 + minutes;
  };

  const parsePrice = (priceString) => {
    if (!priceString || priceString === 'null') return Infinity;
    return parseInt(priceString.replace(/[^0-9]/g, ''));
  };

  // Helper function to check if flight matches selected class
  const matchesClassFilter = (flight, classFilter) => {
    if (classFilter === 'Main') {
      return true; // Main shows all classes
    }

    switch (classFilter) {
      case 'Basic Economy':
        // Basic Economy is only available on international flights
        return flight.routeType === 'international' && flight.mainAvailable;
      case 'Premium Economy':
      case 'Business':
        return flight.premiumAvailable;
      default:
        return true;
    }
  };

  // Filter function
  const getFilteredFlights = (flights) => {
    return flights.filter(flight => {
      // Check stops filter
      const matchesStops = (() => {
        switch (stopsFilter) {
          case 'Nonstop only':
            return flight.stops === 'Nonstop';
          case '1 stop or fewer':
            return flight.stops === 'Nonstop' || flight.stops === '1 stop';
          case '2 stops or fewer':
            return flight.stops === 'Nonstop' || flight.stops === '1 stop' || flight.stops === '2 stops';
          case 'Any number of stops':
          default:
            return true;
        }
      })();

      // Check class filter
      const matchesClass = matchesClassFilter(flight, selectedClass);

      return matchesStops && matchesClass;
    });
  };

  // Sorting and filtering function
  const getSortedFlights = () => {
    let flightsCopy = [...searchResults];

    // Apply filters first
    flightsCopy = getFilteredFlights(flightsCopy);

    if (!sortBy || sortState[sortBy] === 'both') {
      return flightsCopy;
    }

    const isAscending = sortState[sortBy] === 'up';

    return flightsCopy.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'departure':
          aValue = parseTime(a.departureTime);
          bValue = parseTime(b.departureTime);
          break;
        case 'arrival':
          aValue = parseTime(a.arrivalTime);
          bValue = parseTime(b.arrivalTime);
          break;
        case 'duration':
          aValue = parseDuration(a.duration);
          bValue = parseDuration(b.duration);
          break;
        case 'main':
          aValue = parsePrice(a.mainPrice);
          bValue = parsePrice(b.mainPrice);
          break;
        case 'premiumEconomy':
          aValue = parsePrice(a.premiumEconomyPrice);
          bValue = parsePrice(b.premiumEconomyPrice);
          break;
        case 'premium':
          aValue = parsePrice(a.premiumPrice);
          bValue = parsePrice(b.premiumPrice);
          break;
        case 'business':
          aValue = parsePrice(a.businessMiles || a.premiumEconomyPrice);
          bValue = parsePrice(b.businessMiles || b.premiumEconomyPrice);
          break;
        case 'first':
          aValue = parsePrice(a.firstMiles || a.premiumPrice);
          bValue = parsePrice(b.firstMiles || b.premiumPrice);
          break;
        default:
          return 0;
      }

      if (aValue === bValue) return 0;

      if (isAscending) {
        return aValue < bValue ? -1 : 1;
      } else {
        return aValue > bValue ? -1 : 1;
      }
    });
  };

  // Load and sort flights from JSON data
  const flights = getSortedFlights();

  // Check if any flights have business class available for miles mode
  // Business class should only show for very specific routes/conditions
  const hasBusinessFlights = useMemo(() => {
    if (!isRedeemMiles) return false;
    
    // Business class should only be available on select premium routes
    // For example: transcontinental routes (JFK-LAX, JFK-SFO, etc.) or international routes
    const premiumRoutes = ['JFK-LAX', 'LAX-JFK', 'JFK-SFO', 'SFO-JFK', 'EWR-LAX', 'LAX-EWR'];
    
    return flights.some(flight => {
      const route = `${flight.departureCode}-${flight.arrivalCode}`;
      const isPremiumRoute = premiumRoutes.includes(route) || flight.routeType === 'international';
      
      return isPremiumRoute &&
        flight.businessMiles &&
        flight.businessMiles !== 'Not available' &&
        flight.businessAvailable !== false;
    });
  }, [flights, isRedeemMiles]);

  // Helper function to get dynamic fee for each class
  const getFee = (flight, classType) => {
    const defaultFee = '$11.20';
    switch (classType) {
      case 'main':
        return flight.mainFee || defaultFee;
      case 'business':
        return flight.businessFee || defaultFee;
      case 'first':
        return flight.firstFee || defaultFee;
      default:
        return defaultFee;
    }
  };

  const SortArrows = ({ field }) => {
    const currentState = sortState[field] || 'both';
    const isActive = sortBy === field;

    return (
      <div className={`flex flex-col items-center justify-center  border border-[#0078d2] bg-white rounded-[3px]   ${isRedeemMiles ? 'min-h-[20px] min-w-[20px] py-0.2' : 'min-h-[40px] min-w-[32px] gap-0.5 px-1 py-0.5'}`}>
        {(currentState === 'both' || currentState === 'up') && (
          <BiSolidDownArrow
            className={`transform rotate-180 transition-colors ${isActive && currentState === 'up' ? 'text-[#0078d2] justify-start' : 'text-[#0078d2] justify-end'}`}
            size={isRedeemMiles ? 10 : 13}
          />
        )}
        {(currentState === 'both' || currentState === 'down') && (
          <BiSolidDownArrow
            className={`transition-colors ${isActive && currentState === 'down' ? 'text-[#0078d2] justify-start' : 'text-[#0078d2] justify-end'}`}
            size={isRedeemMiles ? 10 : 13}
          />
        )}
      </div>
    );
  };

  const NoFlightsFound = () => (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="mb-6">
        <div className="w-12 h-12 rounded-full border-2 border-gray-400 flex items-center justify-center">
          <span className="text-2xl text-gray-600">⚠</span>
        </div>
      </div>
      <h2 className="text-xl font-medium text-gray-800 mb-2">No flights available</h2>
      <p className="text-gray-600 text-center mb-8">Please try different search filters.</p>
      <button
        onClick={onClearFilters}
        className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors font-medium"
      >
        Clear all filters
      </button>
    </div>
  );

  return (
    <div className="font-sans">
      {/* Sticky Header */}
      <div className={`sticky ${isRedeemMiles ? 'top-[76px] z-20' : 'top-0 z-10'} bg-white`}>
        <div role="group" aria-label="Miles redemption options" className="flex flex-wrap border-b border-t border-gray-300">
          <span
            id="sort-screen-reader-text"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
          >
            Miles redemption view with Main and First class options
          </span>

          {/* Left section - Sort columns */}
          <div className="flex-1 max-w-[41.67%] flex">
            <div className="flex-1 max-w-[33.33%]">
              <button
                className="w-full h-[70px] px-2 flex items-center justify-start bg-transparent border-0 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => handleSort('departure')}
              >
                <span className="sr-only">Sort by depart, not sorted, press to sort early to late</span>
                <div className="flex items-center gap-2">
                  <div className="text-[#0078d2] font-medium text-sm">Depart</div>
                  <SortArrows field="departure" />
                </div>
              </button>
            </div>

            <div className="flex-1 max-w-[33.33%]">
              <button
                className="w-full h-[70px] px-2 flex items-center justify-start bg-transparent border-0 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => handleSort('arrival')}
              >
                <span className="sr-only">Sort by arrive, not sorted, press to sort early to late</span>
                <div className="flex items-center gap-2">
                  <div className="text-[#0078d2] font-medium text-sm">Arrive</div>
                  <SortArrows field="arrival" />
                </div>
              </button>
            </div>

            <div className="flex-1 max-w-[33.33%]">
              <button
                className="w-full h-[70px] px-2 flex items-center justify-start bg-transparent border-0 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => handleSort('duration')}
              >
                <span className="sr-only">Sort by duration, not sorted, press to sort short to long</span>
                <div className="flex items-center gap-2">
                  <div className="text-[#0078d2] font-medium text-sm">Duration</div>
                  <SortArrows field="duration" />
                </div>
              </button>
            </div>
          </div>

          {/* Right section - Miles options */}
          <div className={`flex-1 max-w-[58.33%] flex h-[70px] ${isRedeemMiles ? 'gap-1' : ''}`}>
            <button
              className={`flex-1  hover:bg-[#0656b3] text-white flex  px-4 border-0 cursor-pointer rounded-tl-md rounded-tr-md transition-colors ${isRedeemMiles ? 'bg-[#0061ab] items-center justify-center gap-2 underline'  : 'bg-[#0860c4] items-center justify-between'}`}
              onClick={() => handleSort('main')}
            >
              <span className="sr-only">Sort by Main class miles, not sorted, press to sort miles low to high</span>
              <div className="text-[18px] font-semibold text-[#fff]">Main</div>
              <div className={`flex flex-col items-center justify-center  border border-white bg-[#0860c4] bg-opacity-10 rounded-[3px]   ${isRedeemMiles ? 'min-h-[20px] min-w-[22px] py-0.2' : 'min-h-[40px] min-w-[32px] gap-0.5 px-1 py-0.5'}`}>
                {((sortState['main'] || 'both') === 'both' || (sortState['main'] || 'both') === 'up') && (
                  <BiSolidDownArrow
                    className={`transform rotate-180 transition-colors ${sortBy === 'main' && (sortState['main'] || 'both') === 'up' ? 'text-white' : 'text-white'}`}
                    size={isRedeemMiles ? 10 : 13}
                  />
                )}
                {((sortState['main'] || 'both') === 'both' || (sortState['main'] || 'both') === 'down') && (
                  <BiSolidDownArrow
                    className={`transition-colors ${sortBy === 'main' && (sortState['main'] || 'both') === 'down' ? 'text-white' : 'text-white'}`}
                    size={isRedeemMiles ? 10 : 13}
                  />
                )}
              </div>
            </button>

            {/* Business tab - Only show in miles mode when business flights are available */}
            {isRedeemMiles && hasBusinessFlights && (
              <button
                className={`flex-1  hover:bg-[#0656b3] text-white flex  px-4 border-0 cursor-pointer rounded-tl-md rounded-tr-md transition-colors ${isRedeemMiles ? 'bg-[#0061ab] items-center justify-center gap-2 underline'  : 'bg-[#0860c4] items-center justify-between'}`}
                onClick={() => handleSort('business')}
              >
                <span className="sr-only">Sort by Business class miles, not sorted, press to sort miles low to high</span>
                <div className="text-[18px] font-semibold text-[#fff]">Business</div>
                <div className={`flex flex-col items-center justify-center  border border-white bg-[#0860c4] bg-opacity-10 rounded-[3px]   ${isRedeemMiles ? 'min-h-[20px] min-w-[22px] py-0.2' : 'min-h-[40px] min-w-[32px] gap-0.5 px-1 py-0.5'}`}>
                  {((sortState['business'] || 'both') === 'both' || (sortState['business'] || 'both') === 'up') && (
                    <BiSolidDownArrow
                      className={`transform rotate-180 transition-colors ${sortBy === 'business' && (sortState['business'] || 'both') === 'up' ? 'text-white' : 'text-white'}`}
                      size={isRedeemMiles ? 10 : 13}
                    />
                  )}
                  {((sortState['business'] || 'both') === 'both' || (sortState['business'] || 'both') === 'down') && (
                    <BiSolidDownArrow
                      className={`transition-colors ${sortBy === 'business' && (sortState['business'] || 'both') === 'down' ? 'text-white' : 'text-white'}`}
                      size={isRedeemMiles ? 10 : 13}
                    />
                  )}
                </div>
              </button>
            )}

            {/* Premium Economy tab - Only show in cash mode for international flights */}
            {!isRedeemMiles && !isDomestic && (
              <button
                className="flex-1 bg-[#0860c4] hover:bg-[#0656b3] text-white flex items-center justify-between px-4 border-0 cursor-pointer rounded-tl-md rounded-tr-md transition-colors"
                onClick={() => handleSort('premiumEconomy')}
              >
                <span className="sr-only">Sort by Premium Economy Fare Products, not sorted, press to sort prices low to high</span>
                <div className="text-[16px] font-semibold text-[#fff]">Premium Economy</div>
                <div className={`flex flex-col items-center justify-center  border border-white bg-[#0860c4] bg-opacity-10 rounded-[3px]   ${isRedeemMiles ? 'min-h-[20px] min-w-[22px] py-0.2' : 'min-h-[40px] min-w-[32px] gap-0.5 px-1 py-0.5'}`}>
                  {((sortState['premiumEconomy'] || 'both') === 'both' || (sortState['premiumEconomy'] || 'both') === 'up') && (
                    <BiSolidDownArrow
                      className={`transform rotate-180 transition-colors ${sortBy === 'premiumEconomy' && (sortState['premiumEconomy'] || 'both') === 'up' ? 'text-white' : 'text-white'}`}
                      size={isRedeemMiles ? 10 : 13}
                    />
                  )}
                  {((sortState['premiumEconomy'] || 'both') === 'both' || (sortState['premiumEconomy'] || 'both') === 'down') && (
                    <BiSolidDownArrow
                      className={`transition-colors ${sortBy === 'premiumEconomy' && (sortState['premiumEconomy'] || 'both') === 'down' ? 'text-white' : 'text-white'}`}
                      size={isRedeemMiles ? 10 : 13}
                    />
                  )}
                </div>
              </button>
            )}

            {/* Premium tab - Show in cash mode OR First tab in miles mode */}
            {!isRedeemMiles ? (
              <button
                className="flex-1 bg-[#03264e] hover:bg-[#022045] text-white flex items-center justify-between px-4 border-0 cursor-pointer rounded-tl-md rounded-tr-md transition-colors"
                onClick={() => handleSort('premium')}
              >
                <span className="sr-only">Sort by Premium Fare Products, not sorted, press to sort prices low to high</span>
                <div className="text-[18px] font-semibold text-[#fff]">Premium</div>
                <div className={`flex flex-col items-center justify-center  border border-white bg-[#03264e] bg-opacity-10 rounded-[3px]   ${isRedeemMiles ? 'min-h-[20px] min-w-[22px] py-0.2' : 'min-h-[40px] min-w-[32px] gap-0.5 px-1 py-0.5'}`}>
                  {((sortState['premium'] || 'both') === 'both' || (sortState['premium'] || 'both') === 'up') && (
                    <BiSolidDownArrow
                      className={`transform rotate-180 transition-colors ${sortBy === 'premium' && (sortState['premium'] || 'both') === 'up' ? 'text-white' : 'text-white'}`}
                      size={isRedeemMiles ? 10 : 13}
                    />
                  )}
                  {((sortState['premium'] || 'both') === 'both' || (sortState['premium'] || 'both') === 'down') && (
                    <BiSolidDownArrow
                      className={`transition-colors ${sortBy === 'premium' && (sortState['premium'] || 'both') === 'down' ? 'text-white' : 'text-white'}`}
                      size={isRedeemMiles ? 10 : 13}
                    />
                  )}
                </div>
              </button>
            ) : (
              /* First tab - Only show in miles mode */
              <button
                className={`flex-1  hover:bg-[#0656b3] text-white flex  px-4 border-0 cursor-pointer rounded-tl-md rounded-tr-md transition-colors ${isRedeemMiles ? 'bg-[#00467f] items-center justify-center gap-2 underline'  : 'bg-[#0860c4] items-center justify-between'}`}
                onClick={() => handleSort('first')}
              >
                <span className="sr-only">Sort by First class miles, not sorted, press to sort miles low to high</span>
                <div className="text-[18px] font-semibold text-[#fff]">First</div>
                <div className={`flex flex-col items-center justify-center  border border-white bg-[#03264e] bg-opacity-10 rounded-[3px]   ${isRedeemMiles ? 'min-h-[20px] min-w-[22px] py-0.2' : 'min-h-[40px] min-w-[32px] gap-0.5 px-1 py-0.5'}`}>
                  {((sortState['first'] || 'both') === 'both' || (sortState['first'] || 'both') === 'up') && (
                    <BiSolidDownArrow
                      className={`transform rotate-180 transition-colors ${sortBy === 'first' && (sortState['first'] || 'both') === 'up' ? 'text-white' : 'text-white'}`} 
                      size={isRedeemMiles ? 10 : 13}
                    />
                  )}
                  {((sortState['first'] || 'both') === 'both' || (sortState['first'] || 'both') === 'down') && (
                    <BiSolidDownArrow
                      className={`transition-colors ${sortBy === 'first' && (sortState['first'] || 'both') === 'down' ? 'text-white' : 'text-white'}`}
                      size={isRedeemMiles ? 10 : 13}
                    />
                  )}
                </div>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Flight Results Container */}
      <div className="space-y-4 py-2">
        {flights.length === 0 ? (
          <NoFlightsFound />
        ) : (
          flights.map((flight) => (
            <div key={flight.id}>
              {isRedeemMiles ? (
                // NEW MILES MODE UI - Grid layout based on provided HTML
                <div className="bg-white rounded p-2">
                  <div className="flex">
                    {/* Left side - Flight details (large-5 equivalent) - Aligned with header tabs */}
                    <div className="flex-1 max-w-[41.67%] pr-4">
                      <div className="flex">
                        {/* Departure column - aligned with "Depart" tab */}
                        <div className="flex-1 max-w-[33.33%] text-left">
                          <div className="text-[16px] font-thin text-[#36495a]">{flight.departureCode}</div>
                          <div className="text-[24px] font-thin text-[#36495a]">
                            {flight.departureTime.split(' ')[0]} <span className="text-sm font-thin">{flight.departureTime.split(' ')[1]}</span>
                          </div>
                        </div>

                        {/* Arrival column - aligned with "Arrive" tab */}
                        <div className="flex-1 max-w-[33.33%] text-left">
                          <div className="text-[16px] font-thin text-[#36495a]">{flight.arrivalCode}</div>
                          <div className="text-[24px] font-thin text-[#36495a]">
                            {flight.arrivalTime.split(' ')[0]} <span className="text-sm font-thin">{flight.arrivalTime.split(' ')[1]}</span>
                          </div>
                        </div>

                        {/* Duration column - aligned with "Duration" tab */}
                        <div className="flex-1 max-w-[33.33%] text-left">
                          <div className="font-medium text-gray-800">{flight.duration}</div>
                          <div className="text-[#36495a] cursor-pointer hover:underline text-lg">
                            {flight.stops}
                          </div>
                        </div>
                      </div>

                      {/* Flight segments */}
                      <div className="mt-2">
                        {flight.segments.map((segment, index) => (
                          <div key={index} className="mb-2">
                            <div className="flex items-start text-sm">
                              {/* <div className="mr-2">
                              <BiSolidPlaneAlt aria-hidden="true" className="text-[#333333]" size={16} />
                            </div> */}
                              <div className="leading-relaxed">
                                <span className="text-[#36495a]">{segment.flight}</span>
                                <span className="text-[#d0dae0] mx-1">■</span>
                                <span className="text-[#36495a]">{segment.aircraft}</span>

                              </div>
                            </div>

                            {/* Amenities - keep same as cash mode */}
                            {segment.amenities && segment.amenities.length > 0 && (
                              <div className=" mt-1">
                                <Amenities amenities={segment.amenities} />
                              </div>
                            )}

                            {segment.operator && (
                              <div className="my-3 italic text-xs text-[#333333]">
                                {segment.operator}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Actions */}
                      <div className="mt-2">
                        <button
                          onClick={() => handleFlightDetailsOpen(flight)}
                          aria-haspopup="dialog"
                          className="bg-transparent border-0 text-[#0860c4] cursor-pointer font-thin text-sm underline p-0 hover:underline"
                        >
                          Details
                          <span className="sr-only">, for {flight.departureCode} to {flight.arrivalCode}, departing at {flight.departureTime} {flight.stops}</span>
                        </button>
                      </div>
                    </div>

                    {/* Right side - Available products (large-7 equivalent) */}
                    <div className="flex-1 max-w-[58.33%]">
                      <div className="flex h-full gap-1">
                        {/* Main fare button */}
                        <div className="flex-1 min-h-[110px]">
                          {flight.mainAvailable ? (
                            <button
                              className="w-full h-full py-3 min-h-[110px] cursor-pointer p-3 flex flex-col justify-center text-center transition-all border border-[#d0dae0] rounded-[3px] text-[28px] text-[#0078d2] hover:border-2 hover:border-[#0078d2] my-[5px] font-light"
                              style={{
                                fontFamily: 'AmericanSansLight, Helvetica, Arial, sans-serif',
                                background: 'linear-gradient(to bottom, rgba(208, 218, 224, .3) 0%, rgba(255, 255, 255, .3) 20%, rgba(255, 255, 255, .3) 100%)',
                                height: '100px',
                                minHeight: '110px'
                              }}
                              onClick={() => onFlightSelect?.(flight, 'main')}
                            >
                              <div className="text-[12px] text-[#36495a]">{getTripTypeText()}</div>
                              <div className="text-[28px] font-thin text-[#0078d2]">
                                {flight.mainMiles || '124K'}
                              </div>
                              <div className="text-[14px] text-[#0078d2] font-thin">+ {getFee(flight, 'main')}</div>
                              <div className="text-xs text-orange-600">
                                {flight.mainSeats || '2 seats left'}
                              </div>
                            </button>
                          ) : (
                            <div className="w-full h-full min-h-[110px]  flex items-start justify-center text-gray-500 text-sm rounded-[3px] my-[5px]">
                              Not available
                            </div>
                          )}
                        </div>

                        {/* Business fare button - conditionally rendered */}
                        {hasBusinessFlights && (
                          <div className="flex-1 min-h-[110px]">
                            {flight.businessMiles && flight.businessMiles !== 'Not available' && flight.businessAvailable !== false ? (
                              <button
                                className="w-full h-full py-3 min-h-[110px]  cursor-pointer p-3 flex flex-col justify-center text-center transition-all border border-[#d0dae0] rounded-[3px] text-[28px] text-[#0078d2] hover:border-2 hover:border-[#0078d2] my-[5px] font-light"
                                style={{
                                  fontFamily: 'AmericanSansLight, Helvetica, Arial, sans-serif',
                                  background: 'linear-gradient(to bottom, rgba(208, 218, 224, .3) 0%, rgba(255, 255, 255, .3) 20%, rgba(255, 255, 255, .3) 100%)',
                                  height: '100px',
                                  minHeight: '110px'
                                }}
                                onClick={() => onFlightSelect?.(flight, 'business')}
                              >
                                <div className="text-[12px] text-[#36495a] ">{getTripTypeText()}</div>
                                <div className="text-[28px] font-light text-[#0078d2]">
                                  {flight.businessMiles}
                                </div>
                                <div className="text-[14px] text-[#0078d2]">+ {getFee(flight, 'business')}</div>
                                <div className="text-xs text-orange-600">
                                  {flight.businessSeats || '2 seats left'}
                                </div>
                              </button>
                            ) : (
                              <div className="w-full h-full min-h-[110px]  flex items-start justify-center text-gray-500 text-sm rounded-[3px] my-[5px]">
                                Not available
                              </div>
                            )}
                          </div>
                        )}

                        {/* First fare button */}
                        <div className="flex-1 min-h-[110px]">
                          {flight.firstAvailable !== false ? (
                            <button
                              className="w-full h-full py-3 min-h-[110px] cursor-pointer p-3 flex flex-col justify-center text-center transition-all border border-[#d0dae0] rounded-[3px] text-[28px] text-[#0078d2] hover:border-2 hover:border-[#0078d2] my-[5px] font-light"
                              style={{
                                fontFamily: 'AmericanSansLight, Helvetica, Arial, sans-serif',
                                background: 'linear-gradient(to bottom, rgba(208, 218, 224, .3) 0%, rgba(255, 255, 255, .3) 20%, rgba(255, 255, 255, .3) 100%)',
                                height: '100px',
                                minHeight: '110px'
                              }}
                              onClick={() => onFlightSelect?.(flight, 'first')}
                            >
                              <div className="text-[12px] text-[#36495a]">{getTripTypeText()}</div>
                              <div className="text-[28px] font-light text-[#0078d2] ">
                                {flight.firstMiles || '160K'}
                              </div>
                              {flight.firstMiles && flight.firstMiles !== 'Not available' && (
                                <div className="text-[14px] text-[#0078d2]">+ {getFee(flight, 'first')}</div>
                              )}
                              <div className="text-xs text-orange-600">
                                {flight.firstSeats || '1 seat left'}
                              </div>
                            </button>
                          ) : (
                            <div className="w-full h-full min-h-[110px]  flex items-start justify-center text-gray-500 text-sm rounded-[3px] my-[5px]">
                              Not available
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="w-full border-b border-gray-200 mt-2"></div>
                </div>
              ) : (
                // EXISTING CASH MODE UI
                <div className="flex bg-white rounded" style={{ boxShadow: '0 3px 1px rgba(0, 0, 0, .12), 0 2px 2px rgba(0, 0, 0, .14), 0 1px 5px rgba(0, 0, 0, .2)' }}>
                  {/* Left side - Flight details */}
                  <div tabIndex="-1" className="flex-1 max-w-[41.67%]">
                    <div className="flex flex-wrap p-5 pb-2.5 pl-5 font-sans">
                      {/* Origin */}
                      <div className="flex-shrink-0 text-start">
                        <div className="text-lg font-normal text-[#50657c]">{flight.departureCode}</div>
                        <div className="text-2xl font-bold text-[#181e25]">
                          {flight.departureTime.split(' ')[0]} <span className="text-sm font-bold">{flight.departureTime.split(' ')[1]}</span>
                        </div>
                      </div>

                      {/* Arrow */}
                      <div aria-hidden="true" className="flex items-center justify-center flex-1 max-w-[8.33%]">
                        <span className="text-xl text-gray-600">→</span>
                      </div>

                      {/* Destination */}
                      <div className="flex-1 max-w-[25%] text-center">
                        <div className="text-lg font-normal text-[#50657c]">{flight.arrivalCode}</div>
                        <div className="text-2xl font-bold text-[#181e25]">
                          {flight.arrivalTime.split(' ')[0]} <span className="text-sm font-bold">{flight.arrivalTime.split(' ')[1]}</span>
                        </div>
                      </div>

                      {/* Duration */}
                      <div className="flex-1 max-w-[16.67%] text-center font-medium text-gray-800">{flight.duration}</div>

                      {/* Stops */}
                      <div className="flex-1 max-w-[25%] text-center text-blue-600 cursor-pointer hover:underline">
                        <span className="sr-only">Opens {flight.stops} details for {flight.departureCode} to {flight.arrivalCode}, departing at {flight.departureTime}</span>
                        {flight.stops}
                      </div>

                      {/* Flight segments */}
                      {flight.segments.map((segment, index) => (
                        <div key={index} className="w-full">
                          <div className="flex items-start mb-2 text-sm py-1">
                            <div className="mr-2">
                              <BiSolidPlaneAlt aria-hidden="true" className="text-[#333333]" size={18} />
                            </div>
                            <div className="leading-relaxed">
                              <span className="text-[#181e25] font-thin">{segment.route}</span>
                              <span className="text-[#181e25] mx-1">•</span>
                              <span className="text-[#181e25]"> {segment.flight} </span>
                              <span className="text-[#181e25] mx-1">•</span>
                              <span className="text-[#181e25]"> {segment.aircraft} </span>
                              {segment.operator && (
                                <div className="mt-0.5 mb-0 italic text-xs text-[#333333]">
                                  {segment.operator}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Amenities for this specific segment */}
                          {segment.amenities && segment.amenities.length > 0 && (
                            <div className="w-full mt-1 mb-2 ml-6">
                              <Amenities amenities={segment.amenities} />
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Actions */}
                      <div className="w-full flex items-center gap-2 mt-3">
                        <button
                          onClick={() => handleFlightDetailsOpen(flight)}
                          aria-haspopup="dialog"
                          className="bg-transparent border-0 text-[#0078d2] cursor-pointer text-sm p-0 hover:underline"
                        >
                          <span>Details</span>
                          <span className="sr-only">, for {flight.departureCode} to {flight.arrivalCode}, departing at {flight.departureTime} {flight.stops}</span>
                        </button>
                        <span aria-hidden="true" className="text-gray-300 mx-1">|</span>
                        <button
                          onClick={() => handleSeatSelectionOpen(flight)}
                          aria-haspopup="dialog"
                          className="bg-transparent border-0 text-[#0078d2] cursor-pointer text-sm p-0 hover:underline"
                        >
                          <span>Seats</span>
                          <span className="sr-only">, for {flight.departureCode} to {flight.arrivalCode}, departing at {flight.departureTime} {flight.stops}</span>
                        </button>
                      </div>
                    </div>

                    {/* Alerts */}
                    {flight.alerts.length > 0 && (
                      <div className="bg-gray-50 px-5 py-1.5 border-t border-gray-200 flex items-start gap-2">
                        <span className="text-red-500 flex-shrink-0 mt-0.5">⚠</span>
                        <span className="text-[#181e25] text-[12px]">
                          {flight.alerts.map((alert, index) => (
                            <span key={index}>
                              {alert}
                              {index < flight.alerts.length - 1 && <span>,</span>}
                            </span>
                          ))}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Right side - Product groups */}
                  <div className="flex-1 max-w-[58.33%]">
                    <div className="flex h-full">
                      {isRedeemMiles ? (
                        // Miles mode - three columns: Main, Business, First
                        <>
                          {/* Main fare */}
                          <div className="flex-1">
                            {flight.mainAvailable ? (
                              <button
                                id={`flight-${flight.id}-product-group-MAIN`}
                                aria-expanded="false"
                                className="w-full h-full min-h-[120px] cursor-pointer p-4 flex flex-col justify-center text-center transition-all border border-gray-300 bg-white hover:bg-slate-50 "
                                onClick={() => onFlightSelect?.(flight, 'main')}
                              >
                                <div className="w-full">
                                  <span className="sr-only">Main</span>
                                  <div className="text-xs text-[#36495a]">{getTripTypeText()}</div>
                                  <div className="text-md font-bold text-[#0078d2]">
                                    {flight.mainMiles || '124K'}
                                  </div>
                                  <div className="text-xs text-[#0078d2] font-thin">
                                    + {getFee(flight, 'main')}
                                  </div>
                                  <div className="text-xs text-orange-600">
                                    {flight.mainSeats || '2 seats left'}
                                  </div>
                                </div>
                              </button>
                            ) : (
                              <div className="w-full h-full min-h-[120px] border border-gray-300 bg-gray-50 flex items-center justify-center text-gray-500 text-sm">
                                Not available
                              </div>
                            )}
                          </div>

                          {/* Business fare - Only show if business flights are available */}
                          {hasBusinessFlights && (
                            <div className="flex-1">
                              {flight.businessMiles && flight.businessMiles !== 'Not available' && flight.businessAvailable !== false ? (
                                <button
                                  id={`flight-${flight.id}-product-group-BUSINESS`}
                                  aria-expanded="false"
                                  className="w-full h-full min-h-[120px] cursor-pointer p-4 flex flex-col justify-center text-center transition-all border border-gray-300 bg-white hover:bg-slate-50 "
                                  onClick={() => onFlightSelect?.(flight, 'business')}
                                >
                                  <div className="w-full">
                                    <span className="sr-only">Business</span>
                                    <div className="text-xs text-[#36495a]">{getTripTypeText()}</div>
                                    <div className="text-lg font-bold text-[#0078d2]">
                                      {flight.businessMiles}
                                    </div>
                                    <div className="text-xs text-[#0078d2] font-thin">
                                      + {getFee(flight, 'business')}
                                    </div>
                                    <div className="text-xs text-orange-600">
                                      {flight.businessSeats || '2 seats left'}
                                    </div>
                                  </div>
                                </button>
                              ) : (
                                <div className="w-full h-full min-h-[120px] border border-gray-300 bg-gray-50 flex items-center justify-center text-gray-500 text-sm">
                                  Not available
                                </div>
                              )}
                            </div>
                          )}

                          {/* First fare */}
                          <div className="flex-1">
                            {flight.firstAvailable !== false ? (
                              <button
                                id={`flight-${flight.id}-product-group-FIRST`}
                                aria-expanded="false"
                                className="w-full h-full min-h-[120px] cursor-pointer p-4 flex flex-col justify-center text-center transition-all border border-gray-300 bg-white hover:bg-slate-50 "
                                onClick={() => onFlightSelect?.(flight, 'first')}
                              >
                                <div className="w-full">
                                  <span className="sr-only">First</span>
                                  <div className="text-xs text-[#361495a]">{getTripTypeText()}</div>
                                  <div className="text-md font-bold text-[#0078d2]">
                                    {flight.firstMiles || '160K'}
                                  </div>
                                  {flight.firstMiles && flight.firstMiles !== 'Not available' && (
                                    <div className="text-xs text-[#0078d2] font-thin">
                                      + {getFee(flight, 'first')}
                                    </div>
                                  )}
                                  <div className="text-xs text-orange-600">
                                    {flight.firstSeats || '1 seat left'}
                                  </div>
                                </div>
                              </button>
                            ) : (
                              <div className="w-full h-full min-h-[120px] border border-gray-300 bg-gray-50 flex items-center justify-center text-gray-500 text-sm">
                                Not available
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        // Cash mode - original layout
                        <>
                          {/* Main fare */}
                          <div className="flex-1">
                            {flight.mainAvailable ? (
                              <button
                                id={`flight-${flight.id}-product-group-MAIN`}
                                aria-expanded="false"
                                className={`w-full h-full min-h-[120px] cursor-pointer p-4 flex flex-col justify-start text-left transition-all ${fareDrawer.isOpen && fareDrawer.flightId === flight.id && fareDrawer.fareType === 'main'
                                    ? 'border-2 border-[#0078d2] bg-[#ecf4fe]'
                                    : 'border border-gray-300 bg-white hover:bg-slate-50 hover:border-blue-600'
                                  }`}
                                onClick={() => handleFareDrawerOpen(flight, 'main')}
                              >
                                <div className="w-full">
                                  <span className="sr-only">Main</span>
                                  <div className="flex justify-between items-center mb-2">
                                    <div className="text-sm text-[#333333]">{getTripTypeText()}</div>
                                    <div className="text-[#333333]">
                                      <img
                                        src={fareDrawer.isOpen && fareDrawer.flightId === flight.id && fareDrawer.fareType === 'main'
                                          ? "/content/images/farecard/chevron-up.png"
                                          : "/content/images/farecard/chevron-down.png"}
                                        alt="Toggle fare options"
                                        className="w-4 h-4"
                                      />
                                    </div>
                                  </div>
                                  <div className="mb-2">
                                    <span className="text-2xl font-medium text-[#0078d2]">{flight.mainPrice}</span>
                                  </div>
                                  <div className="text-sm text-[#ae3f13]">
                                    <span className="sr-only">, </span>
                                    <span>{flight.mainSeats}</span>
                                  </div>
                                </div>
                                <span className="sr-only">The lowest fare in the Main fare list starts at {flight.mainPrice} {tripType === 'oneWay' ? 'One way' : 'Round trip'}, per person.</span>
                                <span className="sr-only"> Click here for more fare options </span>
                              </button>
                            ) : (
                              <div className="w-full h-full min-h-[120px] border border-gray-300 bg-gray-50 flex items-center justify-center text-gray-500 text-sm">
                                Not available
                              </div>
                            )}
                          </div>

                          {/* Premium Economy fare - Only show for international flights */}
                          {!isDomestic && (
                            <div className="flex-1">
                              {flight.premiumEconomyAvailable ? (
                                <button
                                  id={`flight-${flight.id}-product-group-PREMIUM-ECONOMY`}
                                  aria-expanded="false"
                                  className={`w-full h-full min-h-[120px] cursor-pointer p-4 flex flex-col justify-start text-left transition-all ${fareDrawer.isOpen && fareDrawer.flightId === flight.id && fareDrawer.fareType === 'premiumEconomy'
                                      ? 'border-2 border-[#0078d2] bg-[#ecf4fe]'
                                      : 'border border-gray-300 bg-white hover:bg-slate-50 hover:border-blue-600'
                                    }`}
                                  onClick={() => handleFareDrawerOpen(flight, 'premiumEconomy')}
                                >
                                  <div className="w-full">
                                    <span className="sr-only">Premium Economy</span>
                                    <div className="flex justify-between items-center mb-2">
                                      <div className="text-sm text-[#333333]">{getTripTypeText()}</div>
                                      <div className="text-[#333333]">
                                        <img
                                          src={fareDrawer.isOpen && fareDrawer.flightId === flight.id && fareDrawer.fareType === 'premiumEconomy'
                                            ? "/content/images/farecard/chevron-up.png"
                                            : "/content/images/farecard/chevron-down.png"}
                                          alt="Toggle fare options"
                                          className="w-4 h-4"
                                        />
                                      </div>
                                    </div>
                                    <div className="mb-2">
                                      <span className="text-2xl font-medium text-[#0078d2]">{flight.premiumEconomyPrice}</span>
                                    </div>
                                    <div className="text-sm text-[#ae3f13] font-medium">
                                      <span className="sr-only">, </span>
                                      <span>{flight.premiumEconomySeats}</span>
                                    </div>
                                  </div>
                                  <span className="sr-only">The lowest fare in the Premium Economy fare list starts at {flight.premiumEconomyPrice} {tripType === 'oneWay' ? 'One way' : 'Round trip'}, per person.</span>
                                  <span className="sr-only"> Click here for more fare options </span>
                                </button>
                              ) : (
                                <div className="w-full h-full min-h-[120px] border border-gray-300 bg-gray-50 flex items-center justify-center text-gray-500 text-sm">
                                  Not available
                                </div>
                              )}
                            </div>
                          )}

                          {/* Premium fare */}
                          <div className="flex-1">
                            {flight.premiumAvailable ? (
                              <button
                                id={`flight-${flight.id}-product-group-PREMIUM`}
                                aria-expanded="false"
                                className={`w-full h-full min-h-[120px] cursor-pointer p-4 flex flex-col justify-start text-left transition-all ${fareDrawer.isOpen && fareDrawer.flightId === flight.id && fareDrawer.fareType === 'premium'
                                    ? 'border-2 border-[#0078d2] bg-[#ecf4fe]'
                                    : 'border border-gray-300 bg-white hover:bg-slate-50 hover:border-blue-600'
                                  }`}
                                onClick={() => handleFareDrawerOpen(flight, 'premium')}
                              >
                                <div className="w-full">
                                  <span className="sr-only">Premium</span>
                                  <div className="flex justify-between items-center mb-2">
                                    <div className="text-sm text-[#333333]">{getTripTypeText()}</div>
                                    <div className="text-[#333333]">
                                      <img
                                        src={fareDrawer.isOpen && fareDrawer.flightId === flight.id && fareDrawer.fareType === 'premium'
                                          ? "/content/images/farecard/chevron-up.png"
                                          : "/content/images/farecard/chevron-down.png"}
                                        alt="Toggle fare options"
                                        className="w-4 h-4"
                                      />
                                    </div>
                                  </div>
                                  <div className="mb-2">
                                    <span className="text-2xl font-medium text-[#0078d2]">{flight.premiumPrice}</span>
                                  </div>
                                  <div className="text-sm text-[#ae3f13] font-medium">
                                    <span className="sr-only">, </span>
                                    <span>{flight.premiumSeats}</span>
                                  </div>
                                </div>
                                <span className="sr-only">The lowest fare in the Premium fare list starts at {flight.premiumPrice} {tripType === 'oneWay' ? 'One way' : 'Round trip'}, per person.</span>
                                <span className="sr-only"> Click here for more fare options </span>
                              </button>
                            ) : (
                              <div className="w-full h-full min-h-[120px] border border-gray-300 bg-gray-50 flex items-center justify-center text-gray-500 text-sm">
                                Not available
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Fare Drawer for this specific flight - only show in cash mode */}
              {!isRedeemMiles && fareDrawer.isOpen && fareDrawer.flightId === flight.id && (
                <FareDrawer
                  isOpen={fareDrawer.isOpen}
                  onClose={handleFareDrawerClose}
                  flightData={fareDrawer.flightData}
                  fareType={fareDrawer.fareType}
                  onSelectFare={handleFareSelection}
                  tripType={tripType}
                />
              )}
            </div>
          )))}
      </div>

      {/* Flight Details Dialog */}
      <FlightDetailsDialog
        isOpen={flightDetailsDialog.isOpen}
        onClose={handleFlightDetailsClose}
        flightData={flightDetailsDialog.flightData}
      />

      {/* Seat Selection Dialog */}
      <SeatSelectionDialog
        isOpen={seatSelectionDialog.isOpen}
        onClose={handleSeatSelectionClose}
        flightData={seatSelectionDialog.flightData}
      />
    </div>
  );
};

export default FlightResults;